import { makeStyles } from '@material-ui/core/styles';

/**
 * The approvals queue and the offer review panel (AXHE-3947).
 *
 * Spacing is on the 2px grid — 16px default, 8px inside the dense approver rows,
 * and the two are never mixed inside one region. Colours come from the FDS theme
 * palette; no raw hex.
 */
export const useStyles = makeStyles(theme => ({
  // --- the toolbar (AXHE-3934) ---

  // One row: the filters left, and room for the search box on the right once
  // the server can take one.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  // 8px between the three controls, not the page's 16px: they are one control
  // group, and spacing them like separate blocks reads as three unrelated
  // buttons. The two densities never meet.
  toolbarFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  // A pill, as the design has it. `borderRadius: 999` so the shape survives the
  // height changing.
  filterPill: {
    borderRadius: 999,
    textTransform: 'none',
    height: 32,
    padding: '0 12px',
    borderColor: theme.palette.common.gray300,
    color: theme.palette.text.primary,
    '& .MuiButton-endIcon': {
      marginLeft: 4,
    },
    '& .MuiButton-endIcon > *:first-child': {
      fontSize: 14,
    },
  },
  // Tinted AND labelled: the button's own text carries the chosen value, so the
  // tint reinforces rather than being the only signal.
  filterPillActive: {
    background: theme.palette.common.blue50,
    borderColor: theme.palette.common.blue600,
    color: theme.palette.common.blue600,
    '&:hover': {
      background: theme.palette.common.blue50,
    },
  },
  filterMenuItemSelected: {
    fontWeight: 500,
  },
  filterMenuCheck: {
    fontSize: 14,
    marginLeft: 8,
  },
  emptyAction: {
    marginTop: 8,
  },

  // Right of the filters, which is the space the toolbar was built with in mind.
  // Grows to fill it rather than taking a fixed width: the filters are three
  // pills of a known size and the search box is what absorbs the rest.
  searchField: {
    flexGrow: 1,
    // Below it the toolbar wraps, and a 120px search box beside three pills is
    // narrower than the placeholder it has to show.
    minWidth: 240,
    maxWidth: 320,
    '& .MuiOutlinedInput-root': {
      height: 32,
      background: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.common.gray300,
    },
  },
  searchIcon: {
    fontSize: 16,
    color: theme.palette.common.gray600,
  },
  searchClear: {
    color: theme.palette.common.gray600,
    // Pulls the target back so the glyph sits on the field's own inset.
    marginRight: -6,
  },

  // Under the table, replacing the "Showing N of M" line the capped list carried.
  pagination: {
    marginTop: 16,
  },
  // ---- the queue ----
  // Matches the rule table above it: the Position column holds account-authored
  // text of a length this page does not choose, so the container scrolls rather
  // than the columns crushing.
  list: {
    marginTop: 16,
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: 4,
    overflowX: 'auto',
  },
  table: {
    tableLayout: 'auto',
  },
  // Matched to the rule table's own header row on the adjacent tab: same band,
  // same height, same divider, same secondary colour, same medium face. Two
  // tables on one page reading at different densities is the "inconsistent
  // density" the guard names. `ApprovalRules/styles.ts` carries the reasoning
  // for the band colour and for taking the weight from the family.
  headerCell: {
    padding: '0 16px',
    height: 40,
    background: theme.palette.common.gray100,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.h4.fontFamily,
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
  },
  // Loading and empty both render inside the table body, so the column headers
  // stay put and the region does not change shape twice per load.
  stateCell: {
    textAlign: 'center',
    padding: 32,
    borderBottom: 'none',
  },
  empty: {
    textAlign: 'center',
    padding: 32,
    borderBottom: 'none',
  },
  emptyBody: {
    marginTop: 8,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    marginTop: 16,
    border: `1px solid ${theme.palette.common.red400}`,
    background: theme.palette.common.red100,
    borderRadius: 4,
  },
  // ---- bulk selection (AXHE-3934) ----
  // Between the toolbar and the table, not floating over it: a pinned bar covers
  // the last row of the page, which is a row the reader may be about to tick.
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
    padding: '8px 16px',
    background: theme.palette.common.blue50,
    border: `1px solid ${theme.palette.common.blue600}`,
    borderRadius: 4,
  },
  // The number is the heading here, not a detail beside the buttons — the reader
  // is about to decide several things at once.
  bulkCount: {
    color: theme.palette.common.blue600,
    fontWeight: 500,
  },
  bulkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  // What a finished bulk run came to. Above the table, like the load error, since
  // it describes the page rather than a row.
  bulkSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 4,
    background: theme.palette.common.gray100,
    border: `1px solid ${theme.palette.common.gray300}`,
  },
  // Narrow, and no padding of its own: a checkbox column that reserves a cell's
  // width pushes the Opening column off the reader's left edge.
  selectCell: {
    width: 48,
    paddingRight: 0,
  },
  // Offer rows only. An opening row is decided from its own cell and opens
  // nothing, so a pointer over it would promise a click that does nothing —
  // Design Requirements §5 locks that asymmetry.
  rowClickable: {
    cursor: 'pointer',
  },
  // The opening name on an offer row, as the design has it: brand blue and
  // medium weight, so the first cell reads as the way into the offer. A real
  // `<button>` rather than a `role="button"` on the `<tr>` — the row also holds
  // a checkbox and the Review control, and nesting those inside a widget is an
  // axe `nested-interactive` violation.
  //
  // Size and family come from `body1`, which is what the plain opening-row name
  // uses: the two lines sit in one column and a link that shrank the name would
  // put it level with the applicant under it.
  nameButton: {
    ...theme.typography.body1,
    // The medium face, not `fontWeight: 500`. This theme carries weight in the
    // family — `CircularXXWeb-Medium` is a family of its own, and `h4` is the
    // role that uses it — so asking for a numeric weight would leave the browser
    // synthesizing a faux-bold out of the Book face.
    fontFamily: theme.typography.h4.fontFamily,
    border: 0,
    margin: 0,
    padding: 0,
    background: 'transparent',
    textAlign: 'left',
    color: theme.palette.common.blue600,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  },
  // Under the applicant line, third in the cell. Same link-styled button as
  // `historyToggle` in the panel — one treatment for a text link in this
  // feature.
  previewLink: {
    ...theme.typography.body2,
    // `inline-flex`, not `flex`: a block-level button in a table cell stretches
    // to the column's width, which would put an invisible click target across
    // the whole cell. The applicant line above it is a block, so it still starts
    // its own line.
    display: 'inline-flex',
    alignItems: 'center',
    // 4px is the icon-to-label gap everywhere; the 8px this container uses
    // between controls would read as two separate things.
    gap: 4,
    marginTop: 4,
    padding: 0,
    border: 0,
    background: 'none',
    color: theme.palette.common.blue600,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  },
  actionsCell: {
    // Wide enough for Reject beside Approve. The Review control on an offer row
    // is narrower, and one width keeps the column edge straight down the table.
    width: 176,
    textAlign: 'right',
  },
  // Approve and Reject on one line, right-aligned with the column. Wraps rather
  // than overflowing: the labels are translated, and a locale that needs two
  // lines gets them.
  rowActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 8,
  },
  // An approval the server refused. Under the buttons rather than beside them —
  // it is a sentence, and the cell is the narrowest on the row.
  rowActionError: {
    marginTop: 4,
    color: theme.palette.common.red600,
    textAlign: 'right',
  },
  // Names the actions column for a screen reader reading the table column by
  // column, without putting a heading on screen the design does not have. Same
  // rule the rule table above it uses.
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // One line per rule in the Approver rule column. Stacked rather than
  // comma-joined: several rules gate one subject, and the cell beside this lists
  // their levels in the same order, so the two read across row by row.
  ruleNames: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },

  // ---- the Approval status column ----
  // One line per level. 4px, not the page's 16px: the lines are one answer read
  // top to bottom, and spacing them further apart reads as separate facts.
  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  // The glyph, the level, and what it came to, on one line. Wraps rather than
  // truncating — a named level plus its state is longer than this column wants to
  // be, and the state is the half a reader came for.
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  // Reinforcement, never the signal: the state's own word sits beside it. Neutral
  // by default, so only a finished level is tinted at all.
  statusIcon: {
    fontSize: 16,
    flexShrink: 0,
    color: theme.palette.common.gray600,
  },
  // green600 and red600 on white, not on a tint — these are glyphs on the table's
  // own background, where the mid-tone tokens clear AA and the pill pairings do
  // not apply.
  statusIconApproved: {
    color: theme.palette.common.green600,
  },
  statusIconRejected: {
    color: theme.palette.common.red600,
  },
  // The level's name at full strength and its state secondary: the reader scans
  // the column for which level, then reads across for what happened.
  statusLevel: {
    color: theme.palette.text.primary,
  },
  // A level nobody can clear. The error role, because it is a misconfiguration a
  // person has to fix rather than a state anybody chose.
  statusAttention: {
    color: theme.palette.common.red600,
  },

  // ---- the review panel ----
  // min(960px, 96vw) per the design. The FDS Drawer already forces 100% width at
  // the `sm` breakpoint, so this governs desktop; the vw cap is what keeps the
  // panel's own footer controls on screen on a narrow window.
  drawer: {
    width: 'min(960px, 96vw)',
  },
  // The panel opens with no top edge otherwise — the same fix SendOffer's drawer
  // applies, and for the same reason: `DrawerHeader` accepts `showShadow` and
  // then ignores it.
  panelHeader: {
    borderBottom: `1px solid ${theme.palette.common.gray300}`,
  },
  panelBody: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 24,
    padding: 24,
  },

  // ---- the approver rail ----
  // 256px per the design, and fixed: the levels and their approvers are the
  // reference column, and letting it flex would trade a stable reading position
  // for a few pixels of letter width.
  rail: {
    width: 256,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  railSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // One rule's ladder. Named only when several rules gate the offer — see
  // ApproverRail — so this is the group, not the label.
  ruleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  ruleName: {
    ...theme.typography.body2,
    color: theme.palette.common.gray800,
  },
  // A level and the people on it. 8px throughout: the heading, the rows and any
  // note are one unit, and the rail's 24px would space them like separate
  // sections.
  level: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingBottom: 12,
  },
  /**
   * The design's uppercase "LEVEL N" heading.
   *
   * Uppercased here rather than by a typography role, and that is a deliberate
   * departure from `content-hierarchy`, which reserves uppercasing to the
   * caption role. That rule describes the Ripple token scale; in this MUI v4
   * theme `caption` is 14px with `textTransform: none` and `overline` is 12px
   * with it explicitly turned off, so no role carries it. The alternative was a
   * pre-uppercased string, which would break every non-Latin locale.
   *
   * The label itself is `levelRow` / `levelRowNamed` from the rule editor's
   * catalog — one sentence for one idea, already translated in every locale.
   */
  levelHeading: {
    ...theme.typography.overline,
    color: theme.palette.common.gray600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  // The level's heading and its own state pill, on one line. The pill sits beside
  // the heading rather than under the approvers so the level's answer is read
  // before the rows that led to it.
  levelHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  // "2 of 3 approved" — only for a level that needs every approver, where the
  // count is the difference between partly done and blocked.
  levelProgress: {
    color: theme.palette.common.gray600,
  },
  // Earlier attempts at this offer. Collapsed by default: the current submission
  // is what an approver is being asked about, and on a first submission there is
  // nothing here at all.
  historyToggle: {
    ...theme.typography.body2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    background: 'none',
    border: 0,
    color: theme.palette.common.blue600,
    cursor: 'pointer',
    textAlign: 'left',
  },
  submission: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 8,
  },
  submissionReason: {
    color: theme.palette.common.gray800,
    fontStyle: 'italic',
    overflowWrap: 'anywhere',
  },
  approverRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  approverIdentity: {
    minWidth: 0,
  },
  // The reason a rejecting approver gave. Indented under their row so it reads
  // as theirs rather than as a note on the level.
  approverComment: {
    marginTop: 4,
    color: theme.palette.common.gray800,
    fontStyle: 'italic',
    overflowWrap: 'anywhere',
  },
  levelNote: {
    color: theme.palette.common.gray600,
  },
  levelWarning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    color: theme.palette.common.orange800,
  },
  warningIcon: {
    fontSize: 16,
    color: theme.palette.common.orange600,
    flexShrink: 0,
    // Aligns the glyph to the first line's mid-point rather than to its box.
    marginTop: 2,
  },

  // ---- status pills ----
  // Colour and a word, never colour alone. The icon is the third cue, for the
  // two states that carry consequence.
  pill: {
    ...theme.typography.overline,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    padding: '2px 8px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  pillApproved: {
    background: theme.palette.common.green100,
    color: theme.palette.common.green800,
  },
  pillRejected: {
    background: theme.palette.common.red100,
    color: theme.palette.common.red800,
  },
  pillPending: {
    background: theme.palette.common.gray200,
    color: theme.palette.common.gray800,
  },
  pillIcon: {
    fontSize: 14,
  },

  // ---- the letter ----
  // The grey backdrop the design puts the letter on, so a white page reads as a
  // document sitting on a surface rather than as the panel's own background.
  letter: {
    flexGrow: 1,
    minWidth: 0,
    background: theme.palette.common.gray200,
    borderRadius: 4,
    padding: 16,
  },
  letterFrame: {
    width: '100%',
    // Fills the drawer's vertical space instead of a fixed height — giving the
    // letter room to read is the point of a panel this wide.
    height: '70vh',
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: 4,
    background: theme.palette.common.white,
    display: 'block',
  },
  letterMissing: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: 24,
    background: theme.palette.common.white,
    borderRadius: 4,
  },

  // ---- the decision footer ----
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    width: '100%',
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
  },
  footerNote: {
    color: theme.palette.common.gray600,
  },
  footerError: {
    color: theme.palette.common.red600,
  },
  footerConfirmed: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.common.green600,
  },
  // The reason field takes the footer's full width: it is a sentence somebody
  // reads later, and a field sized to sit beside the buttons would show two
  // words of it.
  reasonField: {
    width: '100%',
  },
  /**
   * The footer's two groups (Design Requirements §6): editing on the left,
   * deciding on the right.
   *
   * `space-between` with the left slot always rendered — as an empty div when
   * there is no edit control — so the decision buttons keep the same right-hand
   * position whether or not the reader may edit. A left slot that appears and
   * disappears would move Approve under the cursor of somebody who has just
   * been shown a different footer.
   */
  footerGroups: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  footerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  // ---- editing the offer (AXHE-4759) ----
  // The form replaces the letter and the rail both, so it gets the panel's full
  // width. 24px matches `panelBody`, which it stands in for.
  editBody: {
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  /**
   * The three overrides `InlineMessage` needs for a message that wraps.
   *
   * Its root is a MUI v4 `Grid container` at `width: 100%`, it hardcodes
   * `variant="h4"` (14px on a 16px line in this theme), and its icon is an
   * `<svg>` with no intrinsic height inside an `align-items: stretch` row. On
   * one line none of that shows; on the two sentences this banner carries, the
   * copy sets tighter than everything around it and the warning glyph stretches
   * to the full block height, centered against the middle of the paragraph
   * instead of the first line.
   */
  editWarning: {
    '& .MuiTypography-root': {
      lineHeight: '20px',
    },
    '& img': {
      alignSelf: 'flex-start',
      height: 16,
      marginTop: 2,
    },
  },
  // The readiness count and any server refusal, above the controls they explain
  // — the same placement the decision footer gives its own error, and for the
  // same reason: inline they would either clip or push the buttons off the edge.
  editFooterNote: {
    color: theme.palette.common.gray600,
  },
}));
